import { Message } from '@/utils/Message'
import store from '@/store'
import { ErrorTip } from '@/utils/class/ErrorTip'
import { cache } from '@/utils/cacheManager'
import JSEncrypt from 'jsencrypt/bin/jsencrypt'
import {
  getCacheListByKey,
  getFilterCacheList,
  getObjectByCache
} from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'
import $store from '@/store'
import { configList } from '@/api/workflow'
import * as WorkflowConstants from '@/class/cargo/model/parameters/WorkflowConstants'
import * as SystemConstants from '@/class/cargo/model/parameters/SystemConstants'
import { JOB_STATES } from '@/utils/cacheConfig'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'

/**
 * 暂停指定时间（单位：毫秒）
 * @param {Number} millisecond
 */
export function sleep(millisecond) {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve()
    }, millisecond)
  })
}

/**
 * 循环调用，直到传入的方法执行成功
 * @param {Function} func 无参方法，执行成功后的返回值为true
 */
export async function ensureExecuteSuccess(func) {
  if (typeof func !== 'function') {
    console.warn('argument expectation  is a function')
    return
  }
  let isSuccess = func()
  if (!isSuccess) {
    for (let i = 0; i < 100; ++i) {
      await sleep(i * 100)
      isSuccess = func()
      if (isSuccess) {
        break
      }
    }
  }
}

/**
 * 同步管理
 */
export class SyncManager {
  /**
     * @param {Number} waitInterval 等待的间隔时间（单位：毫秒）
     */
  constructor(waitInterval) {
    this.isLocked = false // 是否已锁定
    this.waitInterval = waitInterval || 1
  }
  /**
     * 执行传入的方法
     * @param {Function} func
     */
  async execute(func) {
    if (typeof func !== 'function') {
      return
    }
    if (this.isLocked) {
      await sleep(this.waitInterval)
      await this.execute(func)
      return
    } else {
      this.isLocked = true
      const result = func()
      if (result instanceof Promise) {
        try {
          await result
        } finally {
          this.isLocked = false
        }
      } else {
        this.isLocked = false
      }
    }
  }
}

export class Once {
  constructor(ctx) {
    this.lastTriggeredAt = 0
    this.ctx = ctx
  }
  excute(func, args = [], wait = 500) {
    const currentThis = this
    this.lastTriggeredAt = new Date().getTime()
    setTimeout(() => {
      const now = new Date().getTime()
      if ((now - wait) >= currentThis.lastTriggeredAt) {
        // console.log('回调执行once', func, currentThis.ctx)
        func.call(currentThis.ctx, ...args)
      }
    }, wait)
  }
}

/**
 * 自增长的数值
 */
export class AutoIncrementNum {
  /**
   * @param {Number} initNum 初始值
   */
  constructor(initNum) {
    this.num = initNum || 1
  }
  /**
   * 获取下一个数值
   */
  getNextNum() {
    return this.num++
  }
}

// 全局自增长序号
const globalSeq = new AutoIncrementNum()

/**
 * Document管理
 */
export class DocumentManage {
  constructor() {
    this.scope = 'scope' + globalSeq.getNextNum()
    document[this.scope] = {}
  }
  /**
   * 获取（映射在document上的值）路径
   * @param {String} name
   */
  getPath(name) {
    return `document.${this.scope}.${name}`
  }
  /**
   * 获取映射在document上的值
   * @param {String} name
   */
  get(name) {
    return document[this.scope][name]
  }
  /**
   * @param {String} name
   * @param {any} value 属性或方法
   */
  set(name, value) {
    document[this.scope][name] = value
  }
  destory() {
    document[this.scope] = undefined
  }
}

/**
 * 深拷贝
 * @param {Object} source
 * @param {Map} objMap 已拷贝的对象集合（用于解决循环引用问题），无需传参
 */
export function deepCopy(source, objMap) {
  if (!(source instanceof Object)) {
    return source
  }
  if (!objMap) {
    objMap = new Map()
  }
  const target = Array.isArray(source) ? [] : {}
  objMap.set(source, target)
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      const item = source[key]
      if (item instanceof Object) {
        if (objMap.has(item)) {
          target[key] = objMap.get(item)
        } else {
          target[key] = deepCopy(item, objMap)
        }
      } else {
        target[key] = item
      }
    }
  }
  return target
}

/**
 * 阻止事件的传播
 * @param {Object} e
 */
export function stopBubble(e) {
  if (e && e.stopPropagation) {
    e.stopPropagation()
  } else {
    window.event.cancelBubble = true
  }
}

/**
 * 日期比较
 * @param {String|Date} a
 * @param {String|Date} b
 */
export function dateCompare(a, b) {
  if (typeof a === 'string') {
    if (a.length <= 10) {
      a += ' 00:00:00'
    }
    a = new Date(a)
  }
  if (typeof b === 'string') {
    if (b.length <= 10) {
      b += ' 00:00:00'
    }
    b = new Date(b)
  }
  if ((a instanceof Date) && (b instanceof Date)) {
    return a.getTime() - b.getTime()
  } else {
    return
  }
}

/**
 * 日期比较
 * @param {String|Date} a
 * @param {String|Date} b
 */
export function dateCompareYMD(a, b) {
  let aStr = ''
  if (typeof a === 'string') {
    if (a.length > 10) {
      aStr = a.slice(0, 10)
    } else {
      aStr = a
    }
  } else {
    aStr = a.format('yyyy-MM-DD').toString()
  }
  let bStr = ''
  if (typeof b === 'string') {
    if (b.length > 10) {
      bStr = b.slice(0, 10)
    } else {
      bStr = b
    }
  } else {
    bStr = b.format('yyyy-MM-DD').toString()
  }
  if (aStr === bStr) {
    return 0
  } else if (aStr > bStr) {
    return 1
  } else {
    return -1
  }
}

/**
 * 判断对象是否相等
 * @param a
 * @param b
 */
export function objectIsEqual(a, b) {
  if (a === b) {
    return true
  }
  if (!(a instanceof Object) || !(b instanceof Object)) {
    return false
  }
  if ((a instanceof Array) || (b instanceof Array)) {
    if (!(a instanceof Array) || !(b instanceof Array)) {
      return false
    }
    if (a.length !== b.length) {
      return false
    }
    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) {
        return false
      }
    }
    return true
  }
  for (const key in a) {
    if (a[key] !== b[key]) {
      return false
    }
  }
  return true
}

/**
 * 唯一值容器（保证值相同的对象，其引用也一样）
 */
export class UniqueValue {
  constructor() {
    this.values = []
  }
  /**
   * 检查，如果容器中匹配到传入的值，就将容器中的值返回
   */
  check(val) {
    if (!(val instanceof Object)) {
      return val
    }
    for (const item of this.values) {
      if (objectIsEqual(item, val)) {
        return item
      }
    }
    this.values.push(val)
    return val
  }
}

/**
 * 是否有该菜单
 * @param code 菜单编码
 * @param menus 菜单数组
 */
export function hasMenu(code, menus) {
  if (!menus) {
    menus = store.getters.currentTenantMenus
  }
  for (const item of menus) {
    if (item.code === code || (item.children && hasMenu(code, item.children))) {
      return true
    }
  }
  return false
}

/**
 * 清理循环引用
 * @param {Object} obj 源对象
 * @param {Array} path 方法内部使用，不要传参
 * @returns
 */
export function cleanCircleObject(obj, path) {
  if (!(obj instanceof Object)) {
    return
  }
  if (!path) {
    path = [obj]
  }
  for (const key in obj) {
    const item = obj[key]
    if (item instanceof Object) {
      if (path.includes(item)) {
        if (obj instanceof Array) {
          obj[key] = undefined
        } else {
          delete obj[key]
        }
      } else {
        cleanCircleObject(item, [...path, item])
      }
    }
  }
  if (obj instanceof Array) {
    for (let i = obj.length - 1; i >= 0; --i) {
      if (obj[i] === undefined) {
        obj.splice(i, 1)
      }
    }
  }
}

/**
 * 修复node排序问题
 * @param {Array} nodes
 */
export function repareNodeSort(nodes) {
  nodes.sort((a, b) => {
    const aNo = getNodeSortNo(a)
    const bNo = getNodeSortNo(b)
    if (aNo > bNo) {
      return 1
    } else if (aNo < bNo) {
      return -1
    } else {
      return 0
    }
  })
}

/**
 * 获取node排序编号（从父节点开始，到子节点，拼接childIndex）
 * @param {Object} node
 * @returns
 */
function getNodeSortNo(node) {
  const childIndexArr = []
  while (typeof node.childIndex === 'number') {
    childIndexArr.push(node.childIndex)
    node = node.parent
  }
  childIndexArr.reverse() // 调整顺序，父节点在前，子节点在后
  const maxDigits = String(node.allChildrenCount).length // childIndex最大位数（跳出循环后的node，等于根节点）
  return Array.from(childIndexArr, num => prefixZero(num, maxDigits)).join('')
}

/**
 * 转换为字符串，位数不够，前面补零
 * @param {Number} num 被操作数
 * @param {Number} n 固定的总位数
 * @returns
 */
export function prefixZero(num, n) {
  return (Array(n).join(0) + num).slice(-n)
}

/**
 * 小数点向左移
 * @param {Number} num 被操作数
 * @param {Number} n 移动位数
 * @returns
 */
export function dotShiftLeft(num, n) {
  let str = String(num)
  let sign = ''
  if (str.charAt(0) === '-') {
    str = str.substr(1)
    sign = '-'
  }
  let result = ''
  const dotIndex = str.indexOf('.')
  if (dotIndex < 0) {
    if (str.length <= n) {
      result = '0.' + Array(n - str.length + 1).join(0) + str
    } else {
      result = str.substr(0, str.length - n) + '.' + str.substr(str.length - n)
    }
  } else {
    const str2 = str.substr(0, dotIndex)
    if (str2.length <= n) {
      result = '0.' + Array(n - str2.length + 1).join(0) + str2
    } else {
      result = str2.substr(0, str2.length - n) + '.' + str2.substr(str2.length - n)
    }
    result += str.substr(dotIndex + 1)
  }
  return Number(sign + result)
}

/**
 * 小数点向右移
 * @param {Number} num 被操作数
 * @param {Number} n 移动位数
 * @returns
 */
export function dotShiftRight(num, n) {
  const str = String(num)
  const dotIndex = str.indexOf('.')
  if (dotIndex < 0) {
    return Number(str + Array(n + 1).join(0))
  } else {
    const digits = str.length - (dotIndex + 1)
    if (digits <= n) {
      return Number(str.substr(0, dotIndex) + str.substr(dotIndex + 1) + Array(n - digits + 1).join(0))
    } else {
      return Number(str.substr(0, dotIndex) + str.substr(dotIndex + 1, n) + '.' + str.substr(dotIndex + 1 + n))
    }
  }
}

/**
 * 乘【解决小数相乘丢失精度问题，把小数放到位整数（乘倍数），再缩小回原来倍数（除倍数）】
 * @param {Number} num1 数字1
 * @param {Number} num2 数字2
 */
export function multiply(num1, num2) {
  if (isNaN(num1) || isNaN(num2)) {
    return 0
  }
  const str1 = String(num1)
  const dotIndex1 = str1.indexOf('.')
  const digits1 = str1.length - (dotIndex1 + 1)
  const str2 = String(num2)
  const dotIndex2 = str2.indexOf('.')
  const digits2 = str2.length - (dotIndex2 + 1)
  const result = dotShiftRight(num1, digits1) * dotShiftRight(num2, digits2)
  return dotShiftLeft(result, digits1 + digits2)
}

/**
 * （没有误差的）四舍五入
 * @param {Number} num 数字
 * @param {Number} digits 保留位数
 */
export function roundHalfUp(num, digits = 0) {
  const str = String(num)
  const dotIndex = str.indexOf('.')
  if (dotIndex < 0 || str.length - (dotIndex + 1) <= digits) {
    return num
  }
  return dotShiftLeft(Math.round(dotShiftRight(num, digits)), digits)
}

/**
 * 将source
 * @param source
 * @param target
 * @returns {*}
 */
export function objCopy(source, target) {
  if (!source) {
    return target
  }
  if (!target) {
    return source
  }
  for (const key of Object.keys(source)) {
    if (target.hasOwnProperty(key)) {
      target[key] = source[key]
    }
  }
  return target
}

/**
 * 将source
 * @param source
 * @param target
 * @returns {*}
 */
export function objCopyWithFields(source, target, fields) {
  if (!fields || fields.length === 0) {
    return objCopy(source, target)
  }
  for (const field of fields) {
    if (target.hasOwnProperty(field) && source.hasOwnProperty(field)) {
      target[field] = source[field]
    }
  }
  return target
}

/**
 * 将source里的元素（target不存在的）复制到target里
 * @param source
 * @param target
 * @returns {*}
 */
export function objCopyNoExists(source, target) {
  if (!source) {
    return target
  }
  if (!target) {
    return source
  }
  for (const key of Object.keys(source)) {
    if (!target.hasOwnProperty(key)) {
      target[key] = source[key]
    }
  }
  return target
}

/**
 * 将source里的元素（target不存在的,或为null值的）复制到target里
 * @param source
 * @param target
 * @returns {*}
 */
export function objCopyNoExistsOrNull(source, target) {
  if (!source) {
    return target
  }
  if (!target) {
    return source
  }
  for (const key of Object.keys(source)) {
    if (!target.hasOwnProperty(key) || target[key] === null) {
      target[key] = source[key]
    }
  }
  return target
}
/**
 * 判断两个字符串是否相等，不区分大小写
 * @param source
 * @param target
 * @returns {*}
 */
export function stringEquals(source, target) {
  if (source === target) {
    return true
  }
  if (!source) {
    return false
  }
  if (!target) {
    return false
  }
  return target.toUpperCase() === source.toUpperCase()
}

/**
 * 判断String是否能够转换为Number(用于筛选面板params.value格式化过滤器)
 * @param {String} str 被操作的字符串
 * @returns
 */
export function stringToNum(str) {
  let resault
  if (str.length > 1) {
    const reg = /[\u4e00-\u9fa5a-z]/i
    if (reg.test(str)) {
      return str
    } else {
      if (str.indexOf('-') == 0) {
        const temp = str.substring(1, str.length)
        if (temp.indexOf('0') == 0) {
          if (str.charAt(1) == '.') {
            if (!isNaN(Number(str))) {
              resault = Number(str)
            } else {
              return str
            }
          } else {
            return str
          }
        } else {
          if (!isNaN(Number(str))) {
            resault = Number(str)
          } else {
            return str
          }
        }
      } else if (str.indexOf('0') == 0) {
        if (str.charAt(1) == '.') {
          if (!isNaN(Number(str))) {
            resault = Number(str)
          } else {
            return str
          }
        } else {
          return str
        }
      } else {
        if (!isNaN(Number(str))) {
          resault = Number(str)
        } else {
          return str
        }
      }
    }
  } else {
    if (!isNaN(Number(str))) {
      resault = Number(str)
    } else {
      return str
    }
  }

  return resault
}

/**
 * API缓存管理（只针对GET请求）
 */
export class ApiCacheManage {
  constructor(apiFunc, keyCreator = ApiCacheManage.toSortString) {
    this.cahce = new Map()
    this.callbacks = new Map()
    this.apiFunc = apiFunc
    this.keyCreator = keyCreator
  }
  /**
   * 转换成一个有序（先对对象名称进行排序）的字符串
   */
  static toSortString(params) {
    const arr = []
    for (const name in params) {
      arr.push({ name, value: params[name] })
    }
    arr.sort((a, b) => String(a) - String(b))
    return JSON.stringify(arr)
  }
  /**
   * 执行
   */
  execute(params) {
    let resolveRef
    let rejectRef
    const result = new Promise((resolve, reject) => {
      resolveRef = resolve
      rejectRef = reject
    })
    const key = this.keyCreator(params)
    if (this.cahce.has(key)) {
      resolveRef(this.cahce.get(key))
    } else if (this.callbacks.has(key)) {
      const arr = this.callbacks.get(key)
      arr.push({
        success: res => { resolveRef(res) },
        fail: err => { rejectRef(err) }
      })
    } else {
      this.callbacks.set(key, [{
        success: res => { resolveRef(res) },
        fail: err => { rejectRef(err) }
      }])
      this.apiFunc(params).then(res => {
        const arr = this.callbacks.get(key)
        for (const item of arr) {
          item.success(res)
        }
        this.callbacks.delete(key)
        this.cahce.set(key, res)
      }).catch(err => {
        const arr = this.callbacks.get(key)
        for (const item of arr) {
          item.fail(err)
        }
        this.callbacks.delete(key)
      })
    }
    return result
  }
  clear(key) {
    if (key) {
      this.cahce.delete(key)
    } else {
      this.cahce.clear()
    }
  }
}

/**
 * 捕获错误提示信息
 */
export function catchErrorTip(func) {
  try {
    const result = func()
    if (result instanceof Promise) { // 异步结果处理
      let resolveRef
      let rejectRef
      const asyncResult = new Promise((resolve, reject) => {
        resolveRef = resolve
        rejectRef = reject
      })
      result.then(data => {
        resolveRef(data)
      }).catch(err => {
        if (err instanceof ErrorTip) {
          Message({ message: err.message, type: err.type })
          resolveRef()
        } else {
          rejectRef(err)
        }
      })
      return asyncResult
    }
    return result
  } catch (err) {
    if (err instanceof ErrorTip) {
      Message({ message: err.message, type: err.type })
    } else {
      throw err
    }
  }
}

/**
 * 标识管理
 */
export class MarkManage {
  constructor() {
    this.map = new Map()
    this.autoIncrementNum = new AutoIncrementNum()
  }
  /**
   * 读取标识
   * @param {Number|String} key
   */
  readMark(key) {
    return this.map.get(key)
  }
  /**
   * 重新生成一个标识
   * @param {Number|String} key
   */
  regenerateMark(key) {
    const mark = this.autoIncrementNum.getNextNum()
    this.map.set(key, mark)
    return mark
  }
}

/**
 * 合并数组
 * @param {Array} target
 * @param {Array} source
 */
export function mergeArray(target, source) {
  for (const item of source) {
    if (!target.includes(item)) {
      target.push(item)
    }
  }
}

// 获取排序后的row
export function getSortedRows(gridApi) {
  const result = []
  const nodes = gridApi.rowModel.rowsToDisplay
  for (const node of nodes) {
    if (node.selected) {
      result.push(node.data)
    }
  }
  return result
}

// 获取全部排序后的row
export function getAllSortedRows(gridApi) {
  const nodes = gridApi.rowModel.rowsToDisplay
  return Array.from(nodes, p => p.data)
}

/**
 * 文本转数字数组
 * @param val
 * @returns {[]|*[]}
 */
export function splitToNumberArray(val, splitStr) {
  if (!val) {
    return []
  }
  const array = []
  for (const data of val.split(splitStr)) {
    array.push(Number(data))
  }
  return array
}

/**
 * 文本转数字数组
 * @param val
 * @returns {[]|*[]}
 */
export function split(val, splitStr, isNumber) {
  if (!val) {
    return []
  }
  const array = []
  for (const data of val.split(splitStr)) {
    array.push(isNumber ? Number(data) : data)
  }
  return array
}

// 密码非对称加密
export function encryption(publicKey, passWord) {
  const encryptor = new JSEncrypt()
  encryptor.setPublicKey(publicKey)
  return encryptor.encrypt(passWord)
}

// 密码非对称解密
export function decrypt(privateKey, passWord) {
  const decrypt = new JSEncrypt()
  decrypt.setPrivateKey(privateKey)
  return decrypt.decrypt(passWord)
}

// 获取配置值
export async function getConfigValue(name) {
  await ensureExecuteSuccess(() => cache.config)
  return cache.config.get(name)
}

// 获取配置（数字）值
export async function getConfigNumValue(name) {
  return Number(await getConfigValue(name))
}

// 获取配置（布尔）值
export async function getConfigBoolValue(name) {
  const value = await getConfigValue(name)
  if (!value) {
    return false
  }
  const numValue = Number(value)
  if (typeof numValue === 'number') {
    return numValue !== 0
  }
  return value.trim().toUpperCase() === 'TRUE'
}

// 以post方式提交数据
export function submitDataByPost(url, data) {
  let inputsHtml = ''
  for (const key in data) {
    inputsHtml += `<input type="hidden" name="${key}" value="${data[key]}" />`
  }
  window.open(url).document.write(`
    <html>
      <head></head>
      <body>
        <form id="formId" action="${url}" method="post">
          ${inputsHtml}
        </form>
        <script type="text/javascript">
          document.getElementById("formId").submit();
        </script>
      <body>
    </html>`)
}

// 数据对比箱种转换
export function transformationEqType(harbourType, containerHeightCn, HQHCFlag) {
  if (`${harbourType}${containerHeightCn}` === "RF9'6''") {
    return ['RH']
  }
  if (`${harbourType}${containerHeightCn}` === "GP9'6''") {
    return ['HQ', 'HC']
  }
  if (`${harbourType}${containerHeightCn}` === "GP8'6''") {
    return ['GP']
  }
  if ((harbourType === 'HQ' || harbourType === 'HC') && !containerHeightCn && !HQHCFlag) {
    return ['HQ', 'HC']
  }
  return []
}

// 当前分公司所设置的时区
export function getTimeZone(currentOffice = null) {
  // 当前分公司所设置的时区
  if (!currentOffice || !currentOffice.timezone) return
  const timeZone = getObjectByCache(basicCacheKeys.basic_data_Timezone, 'uuid', currentOffice.timezone)
  if (timeZone) {
    currentOffice.timezoneValue = timeZone.timezone || null
  }
}

// 当前分公司所设置的时区[缓存更新之后]
export function setTimeZoneAfterCache() {
  const currentOffice = $store.getters.currentOffice
  if (currentOffice && !currentOffice.timezoneValue && currentOffice.timezone) {
    getTimeZone(currentOffice)
  }
}

// 接口里面传时区
export function requestTimeZone() {
  return $store.getters.currentOffice.timezoneValue || null
}

/**
 * 字符串转bool类型
 * @param {String} str
 */
export function stringToBool(str) {
  if (typeof str === 'string') {
    if (str && str.trim()) {
      const upperCaseStr = str.trim().toUpperCase()
      return upperCaseStr !== 'FALSE' && upperCaseStr !== '0'
    }
    return false
  }
  return str
}

/**
 * 中文转英文
 * @param {Object} obj
 * @param {Map} zhEnMap
 */
export function chineseToEnglish(obj, zhEnMap) {
  for (const key in obj) {
    const value = obj[key]
    if (value instanceof Object) {
      chineseToEnglish(value, zhEnMap)
    } else if (typeof value === 'string') {
      const valueTrim = value.trim()
      if (zhEnMap.has(valueTrim)) {
        obj[key] = value.replace(valueTrim, zhEnMap.get(valueTrim))
      }
    }
  }
}

/**
 * 字符串转数字数组
 * @param {String} str
 */
export function stringToNumArray(str) {
  const result = []
  const strs = str.split(',')
  for (const s of strs) {
    result.push(Number(s) || 0)
  }
  return result
}

/**
 * 获取数组中空格最少的元素
 * @param arr
 * @returns {*}
 */
export function findMinSpaces(arr) {
  let minSpaces = Infinity
  let minElement
  for (const element of arr) {
    let spaces = 0
    for (const char of element) {
      if (char === ' ') {
        spaces++
      }
    }
    if (spaces < minSpaces) {
      minSpaces = spaces
      minElement = element
    }
  }
  return minElement
}

/**
 * 当前分公司是否开启“业务费用申请单”审批流
 * CARGO-35879 若未开启BPM，业务状态查询条件需隐藏“提交中”
 */
export function setBpmJobCharge() {
  try {
    const currentOffice = $store.getters.currentOffice
    if (!currentOffice || !currentOffice.officeId) {
      return
    }
    const params = {}
    params.documentConfigId = WorkflowConstants.Job_CHARGE.code // 业务费用申请单
    params.officeIdList = [currentOffice.officeId]
    params.runningState = 9 // 是否启用，9:启用，-9禁用
    configList(params).then(res => {
      const list = res.data.list
      const bpmJobCharge = list && list.length > 0
      if (bpmJobCharge) { // 已开启，是否有提交中，没有就加上
        let flag = true
        for (const jobState of JOB_STATES) {
          if (jobState.code === SystemConstants.STATE_COMMITTING.code) {
            flag = false
            break
          }
        }
        if (flag) {
          JOB_STATES.splice(2, 0, SystemConstants.STATE_COMMITTING)
        }
      } else { // 未开启，移除提交中
        for (const jobState of JOB_STATES) {
          if (jobState.code === SystemConstants.STATE_COMMITTING.code) {
            const index = JOB_STATES.indexOf(jobState)
            if (index !== -1) {
              JOB_STATES.splice(index, 1)
            }
          }
        }
      }
    })
  } catch (err) {
    console.log('setBpmJobCharge error', err)
  }
}
export function chargeItemLanguage() {
  const isZh = $store.getters.language === 'zh'
  const office = $store.getters.currentOffice
  // 获取费用项列表
  const chargeItems = getCacheListByKey(TYPE_NAMES.basic_data_ChargeItem)
  // 根据条件匹配语言配置项并添加到费用项对象中
  chargeItems.map(chargeItem => {
    const uuid = chargeItem.uuid
    // 根据费用项uuid获取对应的语言配置列表 "chargeItemUuid", uuid
    const chargeItemLanguages = getFilterCacheList(TYPE_NAMES.basic_data_ChargeItemLanguage, (item) => {
      return item.chargeItemUuid === uuid
    })
    const matchedLanguage = chargeItemLanguages.find(chargeItemLanguage => {
      return office.region && office.region === chargeItemLanguage.regionCode && !isZh
    })

    if (matchedLanguage) {
      chargeItem.enName = matchedLanguage.chargeLanguage
    }

    return chargeItem
  })
  return chargeItems
}

export function handleBlur(type, changeFunc, isModify, isSecond, form) {
  const input = this.$refs[type].$el.querySelector('input')
  if (input) {
    // 获取 title 属性值
    const title = input.getAttribute('title')
    // 如果组件内的input框的title值是10位数，则需要手动给字段赋值
    if (title && title.length === 10) {
      let time = ''
      if (isSecond) {
        time = `${title} 00:00:00`
      } else {
        time = `${title} 00:00`
      }
      // 如果原先就有change事件，则传入change事件进行触发，blur事件触发不会触发change事件
      // 所以要传进来触发
      if (typeof changeFunc === 'function') {
        changeFunc(time)
      }
      // 如果传入true，则代表change事件内没有修改时间字段的逻辑，需要传进来修改
      // 因为传入的时间是10位数的话，不符合format="yyyy-MM-dd HH:mm:ss"的格式，则不会触发change
      // 以及不会触发绑定值的修改，所以需要传入进来进行修改时间值
      if (isModify) {
        this.$set(form, type, time)
      }
    }
  }
}

export function chargeItemCompatibleLanguage() {
  const isZh = $store.getters.language === 'zh'
  const office = $store.getters.currentOffice
  // 获取费用项列表
  const chargeItems = getCacheListByKey(TYPE_NAMES.basic_data_ChargeItem)
  // 根据条件匹配语言配置项并添加到费用项对象中
  chargeItems.map(chargeItem => {
    const uuid = chargeItem.uuid
    // 根据费用项uuid获取对应的语言配置列表 "chargeItemUuid", uuid
    const chargeItemLanguages = getFilterCacheList(TYPE_NAMES.basic_data_ChargeItemLanguage, (item) => {
      return item.chargeItemUuid === uuid
    })
    const matchedLanguage = chargeItemLanguages.find(chargeItemLanguage => {
      return office.region && office.region === chargeItemLanguage.regionCode && !isZh
    })

    if (matchedLanguage) {
      chargeItem.languageName = matchedLanguage.chargeLanguage
    }

    return chargeItem
  })
  return chargeItems
}

// 文本转数组
export function toNumArray(txt) {
  if (txt) {
    return Array.from(txt.split(','), p => Number(p))
  }
  return []
}

// 数组转文本
export function toTxt(strs) {
  if (strs && strs.length > 0) {
    return strs.join(',')
  }
  return ''
}
